import { m } from 'framer-motion';

interface AnswerOptionProps {
  letter: string;
  label: string;
  state: 'idle' | 'selected' | 'correct' | 'incorrect';
  disabled?: boolean;
  onClick?: () => void;
  delay?: number;
}

const PALETTE: Record<AnswerOptionProps['state'], { border: string; bg: string; badgeBg: string; badgeColor: string }> = {
  idle: { border: '#2E2B45', bg: '#1B1830', badgeBg: '#211E3B', badgeColor: '#888' },
  selected: { border: '#39FF88', bg: 'rgba(57,255,136,0.15)', badgeBg: '#39FF88', badgeColor: '#000' },
  correct: { border: '#39FF88', bg: 'rgba(57,255,136,0.15)', badgeBg: '#39FF88', badgeColor: '#000' },
  incorrect: { border: '#FF4D6A', bg: 'rgba(255,77,106,0.15)', badgeBg: '#FF4D6A', badgeColor: '#fff' },
};

// Shared multiple-choice option — used by both the multiplayer question
// screen and the solo quiz, so an answer button looks and feels the same
// regardless of which mode you're playing.
export function AnswerOption({ letter, label, state, disabled, onClick, delay = 0 }: AnswerOptionProps) {
  const palette = PALETTE[state];

  return (
    <m.button
      onClick={onClick}
      disabled={disabled}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay }}
      whileHover={!disabled ? { scale: 1.015, borderColor: '#FFC93C' } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        width: '100%',
        padding: '1.25rem 1.5rem',
        fontSize: '1.1rem',
        fontWeight: 500,
        textAlign: 'left',
        borderRadius: '16px',
        border: `2px solid ${palette.border}`,
        background: palette.bg,
        color: '#fff',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'border-color 0.2s, background 0.2s',
      }}
    >
      <span style={{
        flexShrink: 0,
        width: '2.25rem',
        height: '2.25rem',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '1rem',
        background: palette.badgeBg,
        color: palette.badgeColor,
        transition: 'background 0.2s',
      }}>
        {letter}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {state === 'correct' && <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>✓</span>}
      {state === 'incorrect' && <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>✗</span>}
    </m.button>
  );
}
